import { useState } from 'react'

import { useQuery } from '@apollo/client'

import { getOrderExportIntegrations } from 'utils/transformations'
import { relayConnectionToArray } from 'utils/transformations/graphql'
import { toGlobalId } from 'utils/transformations/graphql'

import { atlasClient } from 'graphql/client'
import { atlas, graphene } from 'graphql/tags'

const salesRepQuery = atlas`#graphql
  query salesRepQuery ($brandId: ID!, $includeInactives: Boolean) {
    brands (filter: { brandIds: [$brandId]  }) {
      edges {
        node {
          id
          brandUsers (filter: { includeInactives: $includeInactives }) {
            edges {
              node {
                id
                displayName
                code
                active
              }
            }
          }
        }
      }
    }
  }
`

// TODO(vaskinyy) remove from orders connections
const OrderListFiltersQuery = graphene`#graphql
  query OrderListFilters {
    collections(filterBy: { archived: false }) {
      edges {
        node {
          id
          name
          code
        }
      }
    }
    viewerBrand {
      id
      types {
        edges {
          node {
            id
            name
            code
          }
        }
      }
      orderEvents {
        edges {
          node {
            id
            name
            code
          }
        }
      }
      orderExportIntegrations {
        edges {
          node {
            id
            isManuallyScheduled
            application {
              id
              name
              integrationType
            }
          }
        }
      }
      divisions {
        edges {
          node {
            id
            code
          }
        }
      }
      priceTypes {
        edges {
          node {
            id
            name
            currency {
              id
              code
            }
          }
        }
      }
    }
  }
`
const RetailerOrderListFiltersQuery = graphene`#graphql
  query RetailerOrderListFilters {
    viewer {
      id
      viewerAccount {
        ... on RetailerAccount {
          id
          brandBuyers {
            edges {
              node {
                id
                name
              }
            }
          }
        }
      }
    }
  }
`

const useSalesReps = (brandId, includeInactives = false) => {
  const { loading, data, error } = useQuery(salesRepQuery, {
    client: atlasClient,
    skip: !brandId,
    variables: {
      brandId: toGlobalId('Brand', brandId),
      includeInactives,
    },
  })

  return {
    salesRepQueryLoading: loading,
    salesReps: relayConnectionToArray(
      data?.brands?.edges?.[0]?.node?.brandUsers ?? [],
    ).filter((brandUser) => brandUser?.displayName),
    salesRepQueryError: error,
  }
}

const getFiltersForBrandUser = (queryResponse, salesRepsData) => {
  const { loading, data, error } = queryResponse
  const { salesReps, salesRepQueryLoading } = salesRepsData

  return {
    orderListFiltersQueryLoading: loading,
    filterData: {
      collections: relayConnectionToArray(data?.collections),
      orderEvents: relayConnectionToArray(data?.viewerBrand?.orderEvents),
      salesRepresentatives: salesRepQueryLoading ? [] : salesReps,
      types: relayConnectionToArray(data?.viewerBrand?.types),
      divisions: relayConnectionToArray(data?.viewerBrand?.divisions),
      orderExportIntegrations:
        !loading && !error && data?.viewerBrand
          ? getOrderExportIntegrations(data.viewerBrand)
          : [],
      priceTypeIds: relayConnectionToArray(data?.viewerBrand?.priceTypes),
    },
    orderListFiltersQueryError: error,
  }
}

const getFiltersForRetailerUser = (queryResponse) => {
  const { loading, data, error } = queryResponse

  return {
    orderListFiltersQueryLoading: loading,
    orderListFiltersQueryError: error,
    filterData: {
      brandBuyers: relayConnectionToArray(
        data?.viewer?.viewerAccount?.brandBuyers,
      ),
    },
  }
}

export const withOrderListFilters = (WrappedComponent) => (props) => {
  const [includeInactives, setIncludeInactivesInOrders] = useState(false)
  const { isBrandUser } = props
  const { currentUser } = props
  const queryResponse = useQuery(
    isBrandUser ? OrderListFiltersQuery : RetailerOrderListFiltersQuery,
  )
  const salesRepsData = useSalesReps(
    currentUser?.account?.accountId,
    includeInactives,
  )

  const customProps = {
    ...props,
    ...(isBrandUser
      ? getFiltersForBrandUser(queryResponse, salesRepsData)
      : getFiltersForRetailerUser(queryResponse)),
    includeInactives,
    setIncludeInactivesInOrders,
  }

  return <WrappedComponent {...customProps} />
}

export default withOrderListFilters
